import * as React from "react";

import { useCurrentProjectContext } from "@editor/contexts/CurrentProjectContext";
import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";

import { useLocation } from "react-router-dom";

import { useSaveHtmlContent } from "./page-cache";
import { useNextDevServerHook } from "./useNextDevServerHook";

type NextDevServerContextType = ReturnType<typeof useNextDevServerHook> & {
  setShouldSaveHtmlContent: (value: boolean) => void;
};

const NextDevServerContext = React.createContext<
  NextDevServerContextType | undefined
>(undefined);

const NextDevServerProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const originalProjectId = useCurrentProjectId();
  const location = useLocation();
  const currentProjectHostingType =
    useCurrentProjectContext()?.project?.hostingType;

  // Set projectId to undefined if URL starts with /editor
  const projectId = location.pathname.startsWith("/editor")
    ? undefined
    : originalProjectId;

  const [shouldSaveHtmlContent, setShouldSaveHtmlContent] =
    React.useState(true);

  useSaveHtmlContent({ projectId, shouldSaveHtmlContent });

  const result = useNextDevServerHook({
    projectId,
    hostingType: currentProjectHostingType,
  });

  return (
    <NextDevServerContext.Provider
      value={{
        ...result,
        setShouldSaveHtmlContent,
      }}
    >
      {children}
    </NextDevServerContext.Provider>
  );
};

function useNextDevServer() {
  const context = React.useContext(NextDevServerContext);
  if (context === undefined) {
    throw new Error(
      "useNextDevServer must be used within a NextDevServerProvider",
    );
  }
  return context;
}

export { NextDevServerProvider, useNextDevServer };
